import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllUsers, deleteUserAsAdmin, setUserAdminStatus } from '../utilities/adminUtils';
import { useAuth } from '../../context/AuthContext';
import PageContainer from '../../../PageUtil/PageContainer';
import { getAuth } from 'firebase/auth';

const UserManagement = () => {
  const { isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (isAdmin) {
      loadUsers();
    }
  }, [isAdmin]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const firestoreUsers = await getAllUsers(); // This now fetches only Firestore data
      
      // Fetch user emails from Firebase Auth
      const auth = getAuth();
      const authUsers = {};
      // This is a simplified approach, in a real app you'd paginate listUsers
      // For web clients, listUsers is typically not directly available or paginated
      // and would require an admin SDK or Callable Cloud Function.
      // For this client-side context, we'll simulate fetching emails.
      // For a real solution on Spark plan, you'd fetch emails individually or via a lookup.
      // For now, we'll try to get the email from the current user object if available,
      // or assume it's not strictly needed for display if removed from Firestore.
      
      // Since we removed email from Firestore, we cannot display it from here.
      // The design choice is to not display email in this table from Firestore.
      
      setUsers(firestoreUsers);
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Failed to load user list');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm(`Are you sure you want to delete all user ${userId} Firestore data? This cannot be undone.`)) {
      try {
        await deleteUserAsAdmin(userId);
        setUsers(users.filter(u => u.id !== userId));
      } catch (err) {
        setError('Failed to delete user');
      }
    }
  };

  const handleToggleAdmin = async (userId, currentStatus) => {
    try {
      await setUserAdminStatus(userId, !currentStatus);
      setUsers(users.map(u => u.id === userId ? { ...u, isAdmin: !currentStatus } : u));
    } catch (err) {
      setError('Failed to update admin status');
    }
  };

  const filteredUsers = users.filter(u => 
    u.email?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    u.id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <PageContainer error={error} setError={setError} isAdmin={isAdmin}>
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer error={error} setError={setError} isAdmin={isAdmin}>
      <div className="py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
            <h1 className="mb-0">User Management</h1>
            <button className="btn btn-outline-secondary" onClick={() => navigate('/admin')}>
                <i className="bi bi-arrow-left me-2"></i>Back to Admin
            </button>
        </div>

        <div className="card shadow-sm">
          <div className="card-header bg-white d-flex justify-content-between align-items-center py-3">
            <h5 className="mb-0">All Users ({users.length})</h5>
            <div className="input-group" style={{ maxWidth: '300px' }}>
              <span className="input-group-text"><i className="bi bi-search"></i></span>
              <input 
                type="text" 
                className="form-control" 
                placeholder="Search by ID or Email" 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>User ID</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map(user => (
                    <tr key={user.id}>
                      <td className="small text-muted">{user.id}</td>
                      <td>
                        {user.isAdmin ? 
                          <span className="badge bg-danger">Admin</span> : 
                          <span className="badge bg-secondary">User</span>
                        }
                      </td>
                      <td>
                        <div className="btn-group btn-group-sm">
                          <button 
                            className="btn btn-outline-primary" 
                            onClick={() => navigate(`/admin/users/${user.id}/portfolios`)}
                            title="View Portfolios"
                          >
                            <i className="bi bi-folder"></i>
                          </button>
                          <button 
                            className="btn btn-outline-success" 
                            onClick={() => navigate(`/admin/users/${user.id}/projects`)}
                            title="View Projects"
                          >
                            <i className="bi bi-kanban"></i>
                          </button>
                          <button 
                            className="btn btn-outline-info" 
                            onClick={() => navigate(`/admin/users/${user.id}/skills`)}
                            title="View Skills"
                          >
                            <i className="bi bi-award"></i>
                          </button>
                          <button 
                            className={`btn ${user.isAdmin ? 'btn-outline-warning' : 'btn-outline-primary'}`}
                            onClick={() => handleToggleAdmin(user.id, user.isAdmin)}
                            title={user.isAdmin ? "Revoke Admin" : "Make Admin"}
                          >
                            <i className={`bi ${user.isAdmin ? 'bi-person-dash' : 'bi-person-plus'}`}></i>
                          </button>
                          <button 
                            className="btn btn-outline-danger" 
                            onClick={() => handleDeleteUser(user.id, user.email)}
                            title="Delete User Data"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredUsers.length === 0 && (
                    <tr>
                      <td colSpan="4" className="text-center py-4">No users found matching "{searchTerm}"</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </PageContainer>
  );
};

export default UserManagement;
